// Forked from ../SpotlightOLD.tsx — keep in sync until phase_0 cleanup.
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { initStickySearch } from '../legacy/initStickySearch';
import { useSpotlightCapabilities } from '../hooks/useSpotlightCapabilities';
import { getCollectionId } from '../helpers/searchUtils';
import { getResponsiveSpotlightWidth } from '../constants';
import { useNLSSearch } from '../NLSSearch/useNLSSearch';
import { isHybridSearchPocSplitOn } from '../HybridSearch/utils';
import { KeyboardNavigationProvider } from '../KeyboardNavigation';
import { SpotlightStateProvider, useSearchState } from '../SpotlightState';
import { useSpotlightDropdown } from '../hooks/useSpotlightDropdown';
import { useRunSearch } from '../hooks/useRunSearch';
import { useSlashKeyShortcut } from '../hooks/useSlashKeyShortcut';
import { useSpotlightSearch } from '../hooks/useSpotlightSearch';
import { useSpotlightClose } from '../hooks/useSpotlightClose';
import { useHybridSearchMode } from './useHybridSearchMode';
import { useSearchByImage } from './useSearchByImage';
import { useResetOnSearchModeChange } from './useResetOnSearchModeChange';
import { SearchBar } from './SearchBar';
import { SearchByImagePanel } from './SearchByImage/SearchByImagePanel';
import { SearchDropdown } from './SearchDropdown';
import { SEARCH_BAR_HEIGHT_CLOSED } from './constants';

export function Spotlight() {
	const refInput = useRef<HTMLInputElement>(null);
	const refContainer = useRef<HTMLDivElement>(null);

	const {
		searchTerm,
		isLoadingResults,
		searchResults,
		selectedSearchOptions,
		selectedResultsCategory,
		dispatch,
		setSearchTerm,
		setSelectedSearchOptions,
		setSelectedResultsCategory,
	} = useSearchState();

	const collectionId = getCollectionId(
		window.location.pathname,
		window.bynder.propsAssetOverview?.collectionId,
	);

	const { canShowStickySearch } = useSpotlightCapabilities();

	const { canUseSimilaritySearch, isNLSSearchOn, setIsNLSSearchOn, isPerformingNLSSearch } =
		useNLSSearch(canShowStickySearch, isHybridSearchPocSplitOn());
	const {
		isHybridSearchEnabled,
		isHybridSearchOn,
		isApplyingSimilarityFilter,
		isDropdownSearchDisabled,
	} = useHybridSearchMode({ isNLSSearchOn, isPerformingNLSSearch });
	const { addSearchTermToRecentSearches, recentSearchTerms } = useRecentSearches({
		isNLSSearchOn: isPerformingNLSSearch,
	});

	const { isSearchByImageOpen, openSearchByImage, closeSearchByImage, returnFromSearchByImage } =
		useSearchByImage({ refInput, dispatch });
	const { isDropdownOpen, openSpotlightDropdown, closeSpotlightDropdown } = useSpotlightDropdown({
		refContainer,
		onCloseSearchByImage: closeSearchByImage,
	});

	const { cancelPendingSearch, onValueChange } = useSpotlightSearch({
		searchTerm,
		setSearchTerm,
		dispatch,
		collectionId,
		selectedSearchOptions,
		isDropdownSearchDisabled,
		isApplyingSimilarityFilter,
		isHybridSearchOn,
		openSpotlightDropdown,
	});

	const showPurpleBorder = isDropdownOpen && isPerformingNLSSearch;

	const openSpotlightAndFocusInput = useCallback(() => {
		openSpotlightDropdown();
		refInput.current?.focus();
	}, [openSpotlightDropdown]);

	useSlashKeyShortcut({ openSpotlight: openSpotlightAndFocusInput, isEnabled: !isDropdownOpen });

	useEffect(() => {
		if (canShowStickySearch) {
			const { searchTerm: stickySearchTerm, searchOptions } = initStickySearch();
			dispatch({
				type: 'stickySearchRestored',
				term: stickySearchTerm,
				options: searchOptions,
			});
		}
	}, [canShowStickySearch, dispatch]);

	useResetOnSearchModeChange({ isNLSSearchOn, dispatch });

	const {
		clearResults,
		onClearButtonClick,
		removeSelectedSearchOption,
		closeAndClearSpotlight,
		onAfterSelect,
	} = useSpotlightClose({
		refInput,
		cancelPendingSearch,
		dispatch,
		openSpotlightDropdown,
		closeSpotlightDropdown,
		isApplyingSimilarityFilter,
		isHybridSearchOn,
		blurOnClose: true,
		reopenOnRemoveOption: true,
	});

	const { defaultSearchAction } = useRunSearch({
		searchTerm,
		selectedSearchOptions,
		isApplyingSimilarityFilter,
		isHybridSearchOn,
		addSearchTermToRecentSearches,
		cleanUp: closeAndClearSpotlight,
	});

	const openSpotlightToSearchByImage = useCallback(() => {
		openSpotlightDropdown();
		openSearchByImage();
	}, [openSpotlightDropdown, openSearchByImage]);

	const spotlightDropdown = {
		isDropdownOpen,
		openSpotlightDropdown,
		closeSpotlightDropdown,
		refInput,
		openSearchByImage: openSpotlightToSearchByImage,
	};

	const search = {
		searchTerm,
		setSearchTerm,
		onValueChange,
		isLoadingResults,
		searchResults,
		clearResults,
		onClearButtonClick,
		defaultSearchAction,
		recentSearchTerms,
		addSearchTermToRecentSearches,
	};

	const scope = {
		selectedSearchOptions,
		setSelectedSearchOptions,
		removeSelectedSearchOption,
		selectedResultsCategory,
		setSelectedResultsCategory,
	};

	const searchMode = {
		canUseSimilaritySearch,
		isNLSSearchOn,
		setIsNLSSearchOn,
		isPerformingNLSSearch,
		isHybridSearchEnabled,
		isHybridSearchOn,
		isDropdownSearchDisabled,
		isSearchOptionsDisabled: isPerformingNLSSearch,
	};

	return (
		<SpotlightStateProvider
			mode={searchMode}
			dropdown={spotlightDropdown}
			search={search}
			scope={scope}
		>
			<KeyboardNavigationProvider
				onEscape={closeSpotlightDropdown}
				onDefaultEnter={defaultSearchAction}
				onAfterSelect={onAfterSelect}
			>
				{isDropdownOpen && <StyledOverlay onClick={closeSpotlightDropdown} />}

				<StyledCard
					ref={refContainer}
					className="nav-bar-search"
					$isOpen={isDropdownOpen}
					$showPurpleBorder={showPurpleBorder}
				>
					{isSearchByImageOpen ? (
						<SearchByImagePanel
							onClickReturn={returnFromSearchByImage}
							onSubmit={closeSpotlightDropdown}
						/>
					) : (
						<>
							<SearchBar collectionId={collectionId} />

							<SearchDropdown />
						</>
					)}
				</StyledCard>
			</KeyboardNavigationProvider>
		</SpotlightStateProvider>
	);
}

const StyledCard = styled.div<{ $isOpen: boolean; $showPurpleBorder: boolean }>`
	${getResponsiveSpotlightWidth()}
	background-color: ${token.white};
	box-sizing: border-box;
	overflow: hidden;
	z-index: 99999;
	border: 1px solid
		${({ $showPurpleBorder }) => ($showPurpleBorder ? token.purple500 : token.gray100)};
	border-radius: 12px;
	box-shadow: ${({ $isOpen }) =>
		$isOpen ? '0 0 0 1px rgba(3, 16, 38, 0.1), 0 2px 8px -1px rgba(3, 16, 38, 0.15)' : ''};
	height: ${({ $isOpen }) => ($isOpen ? 'auto' : `${SEARCH_BAR_HEIGHT_CLOSED}px`)};

	& * {
		box-sizing: border-box;
	}
`;

const StyledOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(3, 16, 38, 0.17);
	z-index: 99998;
`;
